import React, { useState, useEffect } from 'react';
import { toast } from 'react-toastify';
import { Mail, MessageSquare, Save, Eye, EyeOff, Send, Plus, Edit2, Trash2, Check, X } from 'lucide-react';
import axios from 'axios';
import useCommPreflight from '../hooks/useCommPreflight';
import CommPreflightWarning from './CommPreflightWarning';

const API_URL = process.env.REACT_APP_API_URL || '/api';

const CommunicationSettingsEditor = () => {
  const preflight = useCommPreflight();
  const [settings, setSettings] = useState({
    // Email Settings
    emailProvider: 'smtp', // smtp, sendgrid, mailgun
    smtpHost: '',
    smtpPort: '587',
    smtpSecure: false,
    smtpUser: '',
    smtpPassword: '',
    emailFromAddress: '',
    emailFromName: '',
    sendgridApiKey: '',
    mailgunApiKey: '',
    mailgunDomain: '',
    
    // SMS Settings
    smsProvider: 'twilio', // twilio, vonage, aws-sns
    twilioAccountSid: '',
    twilioAuthToken: '',
    twilioPhoneNumber: '',
    vonageApiKey: '',
    vonageApiSecret: '',
    vonageFromNumber: '',
    awsSnsAccessKey: '',
    awsSnsSecretKey: '',
    awsSnsRegion: 'us-east-1',
    
    // Notification Preferences
    sendAppointmentConfirmations: true,
    sendAppointmentReminders: true,
    sendCancellationNotifications: true,
    attachInvoiceToConfirmation: false,
    reminderHoursBefore: 24,
    
    // Admin Notifications
    admin_notification_email: [], // Array of email addresses
    admin_notifications_enabled: false,
    admin_notification_new_booking: true,
    admin_notification_approval: true,
    admin_notification_cancellation: true,
    admin_notification_status_change: true,
    admin_notification_financial: true,
  });

  const [showPasswords, setShowPasswords] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState({ email: false, sms: false });
  const [newEmail, setNewEmail] = useState('');
  const [editingEmailIndex, setEditingEmailIndex] = useState(null);
  const [editingEmailValue, setEditingEmailValue] = useState('');

  useEffect(() => {
    loadSettings();
  }, []);

  const loadSettings = async () => {
    try {
      const token = sessionStorage.getItem('token');
      const response = await axios.get(`${API_URL}/communication-settings`, {
        headers: { Authorization: `Bearer ${token}` },
      });

      if (response.data) {
        setSettings(prev => ({ ...prev, ...response.data }));
      }
    } catch (error) {
      console.error('Load settings error:', error);
      // Keep defaults if not found
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (field, value) => {
    setSettings(prev => ({ ...prev, [field]: value }));
  };

  const togglePasswordVisibility = (field) => {
    setShowPasswords(prev => ({ ...prev, [field]: !prev[field] }));
  };

  // Email management functions
  const addEmail = () => {
    if (!newEmail.trim()) {
      toast.error('Please enter an email address');
      return;
    }
    
    // Basic email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(newEmail)) {
      toast.error('Please enter a valid email address');
      return;
    }

    // Check for duplicates
    if (settings.admin_notification_email.includes(newEmail)) {
      toast.error('This email is already in the list');
      return;
    }

    setSettings(prev => ({
      ...prev,
      admin_notification_email: [...prev.admin_notification_email, newEmail]
    }));
    setNewEmail('');
    toast.success('Email added');
  };

  const startEditEmail = (index) => {
    setEditingEmailIndex(index);
    setEditingEmailValue(settings.admin_notification_email[index]);
  };

  const saveEditEmail = () => {
    if (!editingEmailValue.trim()) {
      toast.error('Please enter an email address');
      return;
    }

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(editingEmailValue)) {
      toast.error('Please enter a valid email address');
      return;
    }

    // Check for duplicates (excluding the current one being edited)
    const otherEmails = settings.admin_notification_email.filter((_, i) => i !== editingEmailIndex);
    if (otherEmails.includes(editingEmailValue)) {
      toast.error('This email is already in the list');
      return;
    }

    const updatedEmails = [...settings.admin_notification_email];
    updatedEmails[editingEmailIndex] = editingEmailValue;
    
    setSettings(prev => ({
      ...prev,
      admin_notification_email: updatedEmails
    }));
    
    setEditingEmailIndex(null);
    setEditingEmailValue('');
    toast.success('Email updated');
  };

  const cancelEditEmail = () => {
    setEditingEmailIndex(null);
    setEditingEmailValue('');
  };

  const deleteEmail = (index) => {
    const updatedEmails = settings.admin_notification_email.filter((_, i) => i !== index);
    setSettings(prev => ({
      ...prev,
      admin_notification_email: updatedEmails
    }));
    toast.success('Email removed');
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const token = sessionStorage.getItem('token');
      await axios.put(
        `${API_URL}/communication-settings`,
        settings,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Communication settings saved successfully!');
      // Re-check preflight so any feature toggles that were gated on email/SMS
      // configuration become enabled immediately on this same screen.
      preflight.refresh();
    } catch (error) {
      console.error('Save error:', error);
      toast.error('Failed to save settings');
    } finally {
      setSaving(false);
    }
  };

  const handleTestEmail = async () => {
    // Validate settings before testing
    if (!settings.emailProvider) {
      toast.error('Please select an email provider first');
      return;
    }
    
    if (settings.emailProvider === 'smtp' && (!settings.smtpHost || !settings.smtpUser || !settings.smtpPassword)) {
      toast.error('Please fill in all SMTP settings (Host, Username, Password) and SAVE before testing');
      return;
    }

    if (!settings.emailFromAddress) {
      toast.error('Please enter a "From Email Address" to receive the test email');
      return;
    }

    setTesting(prev => ({ ...prev, email: true }));
    try {
      const token = sessionStorage.getItem('token');
      await axios.post(
        `${API_URL}/communication-settings/test-email`,
        { testEmail: settings.emailFromAddress },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(`Test email sent to ${settings.emailFromAddress}! Check your inbox.`);
    } catch (error) {
      const errorMsg = error.response?.data?.error || 'Failed to send test email';
      const errorDetails = error.response?.data?.details;
      
      if (errorMsg.includes('not configured') || errorMsg.includes('incomplete')) {
        toast.error(`${errorMsg}. Make sure to click "Save Communication Settings" first!`, { autoClose: 5000 });
      } else if (errorDetails) {
        toast.error(`${errorMsg}: ${errorDetails}`, { autoClose: 7000 });
      } else {
        toast.error(errorMsg);
      }
    } finally {
      setTesting(prev => ({ ...prev, email: false }));
    }
  };

  const handleTestSMS = async () => {
    setTesting(prev => ({ ...prev, sms: true }));
    try {
      const token = sessionStorage.getItem('token');
      const testNumber = prompt('Enter phone number to test (with country code, e.g., +1234567890):');
      if (!testNumber) return;

      await axios.post(
        `${API_URL}/communication-settings/test-sms`,
        { testNumber },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Test SMS sent!');
    } catch (error) {
      toast.error(error.response?.data?.error || 'Failed to send test SMS');
    } finally {
      setTesting(prev => ({ ...prev, sms: false }));
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-8">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  return (
    <div className="card">
      <div className="flex items-center space-x-3 mb-4">
        <Mail className="text-primary-600" size={24} />
        <h2 className="text-2xl font-bold">Email Settings</h2>
      </div>

      <p className="text-gray-600 mb-6">
        Configure your email provider for sending appointment notifications, reminders, and communications to clients.
      </p>

      <div className="space-y-8">
        {/* Email Settings */}
        <div>
          <div className="flex items-center space-x-2 mb-4">
            <Mail size={20} className="text-primary-600" />
            <h3 className="text-xl font-bold">Email Configuration</h3>
          </div>

          <div className="space-y-4">
            {/* Email Provider Selection */}
            <div>
              <label className="label">Email Provider</label>
              <select
                value={settings.emailProvider}
                onChange={(e) => handleChange('emailProvider', e.target.value)}
                className="input"
              >
                <option value="smtp">SMTP (Gmail, Outlook, etc.)</option>
                <option value="sendgrid">SendGrid</option>
                <option value="mailgun">Mailgun</option>
              </select>
            </div>

            {/* SMTP Settings */}
            {settings.emailProvider === 'smtp' && (
              <div className="bg-gray-50 p-4 rounded-lg space-y-3">
                <h4 className="font-semibold">SMTP Server Settings</h4>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  <div>
                    <label className="label">SMTP Host</label>
                    <input
                      type="text"
                      value={settings.smtpHost}
                      onChange={(e) => handleChange('smtpHost', e.target.value)}
                      className="input"
                      placeholder="smtp.gmail.com"
                    />
                  </div>
                  <div>
                    <label className="label">SMTP Port</label>
                    <input
                      type="number"
                      value={settings.smtpPort}
                      onChange={(e) => handleChange('smtpPort', e.target.value)}
                      className="input"
                      placeholder="587"
                    />
                  </div>
                </div>

                <div className="flex items-center space-x-2">
                  <input
                    type="checkbox"
                    id="smtpSecure"
                    checked={settings.smtpSecure}
                    onChange={(e) => handleChange('smtpSecure', e.target.checked)}
                    className="w-4 h-4 text-primary-600 rounded"
                  />
                  <label htmlFor="smtpSecure" className="text-sm font-medium text-gray-700">
                    Use SSL/TLS (Port 465)
                  </label>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  <div>
                    <label className="label">SMTP Username/Email</label>
                    <input
                      type="text"
                      value={settings.smtpUser}
                      onChange={(e) => handleChange('smtpUser', e.target.value)}
                      className="input"
                      placeholder="your-email@gmail.com"
                    />
                  </div>
                  <div>
                    <label className="label">SMTP Password</label>
                    <div className="relative">
                      <input
                        type={showPasswords.smtpPassword ? 'text' : 'password'}
                        value={settings.smtpPassword}
                        onChange={(e) => handleChange('smtpPassword', e.target.value)}
                        className="input pr-10"
                        placeholder="••••••••"
                      />
                      <button
                        type="button"
                        onClick={() => togglePasswordVisibility('smtpPassword')}
                        className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400"
                      >
                        {showPasswords.smtpPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                      </button>
                    </div>
                  </div>
                </div>

                {/* Email Provider Setup Instructions */}
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mt-3">
                  <h5 className="font-semibold text-blue-900 mb-3">📧 Setup Guide — Choose Your Email Provider</h5>
                  
                  {/* Gmail */}
                  <div className="mb-4 pb-4 border-b border-blue-200">
                    <h6 className="font-bold text-blue-800 mb-2">Gmail (Recommended)</h6>
                    <div className="bg-white rounded p-3 space-y-2">
                      <p className="text-xs font-semibold text-gray-700">Enter these settings above:</p>
                      <div className="grid grid-cols-2 gap-1 text-xs text-gray-600">
                        <span>• <strong>SMTP Host:</strong> <code className="bg-gray-100 px-1 rounded">smtp.gmail.com</code></span>
                        <span>• <strong>SMTP Port:</strong> <code className="bg-gray-100 px-1 rounded">587</code></span>
                        <span>• <strong>Username:</strong> Your full Gmail address</span>
                        <span>• <strong>SSL/TLS:</strong> Leave unchecked</span>
                      </div>
                      <div className="mt-2 pt-2 border-t border-gray-200">
                        <p className="text-xs font-semibold text-red-700 mb-1">⚠️ Password — You must use an App Password, NOT your regular Gmail password:</p>
                        <ol className="text-xs text-gray-700 space-y-2 list-decimal list-inside ml-1">
                          <li>
                            <strong>Turn on 2-Step Verification</strong> (required first) — 
                            Go to <a href="https://myaccount.google.com/security" target="_blank" rel="noopener noreferrer" className="text-blue-600 underline font-medium">myaccount.google.com/security</a> → 
                            scroll to "How you sign in to Google" → click "2-Step Verification" → follow the prompts to enable it
                          </li>
                          <li>
                            <strong>Generate an App Password</strong> — 
                            Go to <a href="https://myaccount.google.com/apppasswords" target="_blank" rel="noopener noreferrer" className="text-blue-600 underline font-medium">myaccount.google.com/apppasswords</a> → 
                            type a name like "Morphbook" → click "Create" → Google will show a <strong>16-character password</strong> (e.g. <code className="bg-gray-100 px-1 rounded">abcd efgh ijkl mnop</code>)
                          </li>
                          <li>
                            <strong>Copy that 16-character password</strong> and paste it into the <strong>"SMTP Password"</strong> field above (spaces are fine — Gmail ignores them)
                          </li>
                        </ol>
                      </div>
                    </div>
                  </div>

                  {/* Outlook/Hotmail */}
                  <div className="mb-4 pb-4 border-b border-blue-200">
                    <h6 className="font-bold text-blue-800 mb-2">Outlook / Hotmail / Live</h6>
                    <div className="bg-white rounded p-3 space-y-1">
                      <div className="grid grid-cols-2 gap-1 text-xs text-gray-600">
                        <span>• <strong>SMTP Host:</strong> <code className="bg-gray-100 px-1 rounded">smtp-mail.outlook.com</code></span>
                        <span>• <strong>SMTP Port:</strong> <code className="bg-gray-100 px-1 rounded">587</code></span>
                        <span>• <strong>Username:</strong> Your full email address</span>
                        <span>• <strong>Password:</strong> Your regular password</span>
                      </div>
                    </div>
                  </div>

                  {/* Yahoo */}
                  <div className="mb-4 pb-4 border-b border-blue-200">
                    <h6 className="font-bold text-blue-800 mb-2">Yahoo Mail</h6>
                    <div className="bg-white rounded p-3 space-y-1">
                      <div className="grid grid-cols-2 gap-1 text-xs text-gray-600">
                        <span>• <strong>SMTP Host:</strong> <code className="bg-gray-100 px-1 rounded">smtp.mail.yahoo.com</code></span>
                        <span>• <strong>SMTP Port:</strong> <code className="bg-gray-100 px-1 rounded">587</code></span>
                      </div>
                      <p className="text-xs text-red-700 mt-1">
                        ⚠️ Yahoo also requires an App Password (not your regular password). Generate one at 
                        <a href="https://login.yahoo.com/account/security" target="_blank" rel="noopener noreferrer" className="text-blue-600 underline"> Yahoo Account Security</a> → "Generate app password".
                      </p>
                    </div>
                  </div>

                  {/* Shaw */}
                  <div className="mb-4 pb-4 border-b border-blue-200">
                    <h6 className="font-bold text-blue-800 mb-2">Shaw Email</h6>
                    <div className="bg-white rounded p-3 space-y-1">
                      <div className="grid grid-cols-2 gap-1 text-xs text-gray-600">
                        <span>• <strong>SMTP Host:</strong> <code className="bg-gray-100 px-1 rounded">smtp.shaw.ca</code></span>
                        <span>• <strong>SMTP Port:</strong> <code className="bg-gray-100 px-1 rounded">587</code></span>
                        <span>• <strong>Username:</strong> Your Shaw email address</span>
                        <span>• <strong>Password:</strong> Your Shaw email password</span>
                      </div>
                    </div>
                  </div>

                  {/* Generic */}
                  <div className="mb-3">
                    <h6 className="font-bold text-blue-800 mb-2">Other Providers</h6>
                    <p className="text-xs text-gray-600">
                      Contact your email provider for SMTP settings. Most providers use port <strong>587</strong> (TLS/STARTTLS) or <strong>465</strong> (SSL — check "Use SSL/TLS" above if using port 465).
                    </p>
                  </div>

                  <div className="bg-red-50 border border-red-300 rounded p-3 mt-3">
                    <p className="text-xs text-red-800 font-semibold">
                      ⚠️ After filling in your settings, scroll to the bottom and click "Save Communication Settings" BEFORE clicking "Send Test Email".
                    </p>
                  </div>
                </div>
              </div>
            )}

            {/* SendGrid Settings */}
            {settings.emailProvider === 'sendgrid' && (
              <div className="bg-gray-50 p-4 rounded-lg space-y-3">
                <h4 className="font-semibold">SendGrid API Settings</h4>
                <div>
                  <label className="label">SendGrid API Key</label>
                  <div className="relative">
                    <input
                      type={showPasswords.sendgridApiKey ? 'text' : 'password'}
                      value={settings.sendgridApiKey}
                      onChange={(e) => handleChange('sendgridApiKey', e.target.value)}
                      className="input pr-10"
                      placeholder="SG.••••••••"
                    />
                    <button
                      type="button"
                      onClick={() => togglePasswordVisibility('sendgridApiKey')}
                      className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400"
                    >
                      {showPasswords.sendgridApiKey ? <EyeOff size={18} /> : <Eye size={18} />}
                    </button>
                  </div>
                </div>
              </div>
            )}

            {/* Mailgun Settings */}
            {settings.emailProvider === 'mailgun' && (
              <div className="bg-gray-50 p-4 rounded-lg space-y-3">
                <h4 className="font-semibold">Mailgun API Settings</h4>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  <div>
                    <label className="label">Mailgun API Key</label>
                    <div className="relative">
                      <input
                        type={showPasswords.mailgunApiKey ? 'text' : 'password'}
                        value={settings.mailgunApiKey}
                        onChange={(e) => handleChange('mailgunApiKey', e.target.value)}
                        className="input pr-10"
                        placeholder="key-••••••••"
                      />
                      <button
                        type="button"
                        onClick={() => togglePasswordVisibility('mailgunApiKey')}
                        className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400"
                      >
                        {showPasswords.mailgunApiKey ? <EyeOff size={18} /> : <Eye size={18} />}
                      </button>
                    </div>
                  </div>
                  <div>
                    <label className="label">Mailgun Domain</label>
                    <input
                      type="text"
                      value={settings.mailgunDomain}
                      onChange={(e) => handleChange('mailgunDomain', e.target.value)}
                      className="input"
                      placeholder="mg.yourdomain.com"
                    />
                  </div>
                </div>
              </div>
            )}

            {/* From Address */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <div>
                <label className="label">From Email Address</label>
                <input
                  type="email"
                  value={settings.emailFromAddress}
                  onChange={(e) => handleChange('emailFromAddress', e.target.value)}
                  className="input"
                  placeholder="noreply@yourbusiness.com"
                />
              </div>
              <div>
                <label className="label">From Name</label>
                <input
                  type="text"
                  value={settings.emailFromName}
                  onChange={(e) => handleChange('emailFromName', e.target.value)}
                  className="input"
                  placeholder="Your Business Name"
                />
              </div>
            </div>

            {/* Test Email Button */}
            <button
              onClick={handleTestEmail}
              disabled={testing.email || !settings.emailFromAddress}
              className="btn bg-blue-100 text-blue-700 hover:bg-blue-200 flex items-center space-x-2"
            >
              <Send size={18} />
              <span>{testing.email ? 'Sending...' : 'Send Test Email'}</span>
            </button>
          </div>
        </div>

        {/* SMS Settings - Moved to dedicated SMS Settings panel */}
        <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg">
          <p className="text-sm text-blue-800">
            <strong>📱 SMS Settings:</strong> SMS configuration has been moved to the dedicated <strong>SMS Settings</strong> section below for better organization.
            Scroll down to configure Twilio, Vonage, or AWS SNS for text notifications.
          </p>
        </div>

        {/* Notification Preferences */}
        <div>
          <h3 className="text-xl font-bold mb-4">Notification Preferences</h3>
          <div className="bg-gray-50 p-4 rounded-lg space-y-3">
            <div className="flex items-center space-x-2">
              <input
                type="checkbox"
                id="sendConfirmations"
                checked={settings.sendAppointmentConfirmations}
                onChange={(e) => handleChange('sendAppointmentConfirmations', e.target.checked)}
                className="w-4 h-4 text-primary-600 rounded"
              />
              <label htmlFor="sendConfirmations" className="text-sm font-medium text-gray-700">
                Send appointment confirmation emails/SMS
              </label>
            </div>

            <div className="flex items-center space-x-2">
              <input
                type="checkbox"
                id="sendReminders"
                checked={settings.sendAppointmentReminders}
                onChange={(e) => handleChange('sendAppointmentReminders', e.target.checked)}
                className="w-4 h-4 text-primary-600 rounded"
              />
              <label htmlFor="sendReminders" className="text-sm font-medium text-gray-700">
                Send appointment reminder emails/SMS
              </label>
            </div>

            {settings.sendAppointmentReminders && (
              <div className="ml-6">
                <label className="label">Send reminders how many hours before appointment?</label>
                <input
                  type="number"
                  value={settings.reminderHoursBefore}
                  onChange={(e) => handleChange('reminderHoursBefore', e.target.value)}
                  className="input max-w-xs"
                  min="1"
                  max="168"
                />
              </div>
            )}

            <div className="flex items-center space-x-2">
              <input
                type="checkbox"
                id="sendCancellations"
                checked={settings.sendCancellationNotifications}
                onChange={(e) => handleChange('sendCancellationNotifications', e.target.checked)}
                className="w-4 h-4 text-primary-600 rounded"
              />
              <label htmlFor="sendCancellations" className="text-sm font-medium text-gray-700">
                Send cancellation notification emails/SMS
              </label>
            </div>

            <div className="pt-3 border-t border-gray-200">
              {/* Preflight: attaching the invoice obviously requires email
                  to actually go out. Block enablement until SMTP is set. */}
              {!preflight.loading && !preflight.emailConfigured && !settings.attachInvoiceToConfirmation && (
                <CommPreflightWarning
                  channel="email"
                  reason={preflight.emailReason}
                  featureName="invoice PDF attachments"
                />
              )}
              <div className={`flex items-start space-x-2 ${
                !preflight.emailConfigured && !settings.attachInvoiceToConfirmation ? 'opacity-60' : ''
              }`}>
                <input
                  type="checkbox"
                  id="attachInvoiceToConfirmation"
                  checked={!!settings.attachInvoiceToConfirmation}
                  disabled={!preflight.emailConfigured && !settings.attachInvoiceToConfirmation}
                  onChange={(e) => {
                    if (e.target.checked && !preflight.emailConfigured) {
                      toast.error('Configure email settings above before enabling invoice attachments.');
                      return;
                    }
                    handleChange('attachInvoiceToConfirmation', e.target.checked);
                  }}
                  className="w-4 h-4 text-primary-600 rounded mt-0.5"
                />
                <label htmlFor="attachInvoiceToConfirmation" className="text-sm text-gray-700">
                  <span className="font-medium">Attach invoice PDF to appointment confirmation email</span>
                  <p className="text-xs text-gray-500 mt-0.5">
                    When enabled, paid (or partially-paid) appointments include the
                    rendered invoice / receipt as a PDF attachment on the
                    confirmation email. Unpaid bookings still send a normal
                    confirmation without an attachment. Templates are configured
                    under Settings → Invoice Templates.
                  </p>
                </label>
              </div>
            </div>
          </div>
        </div>

        {/* Admin Notifications */}
        <div className="card">
          <h3 className="text-xl font-semibold mb-4 flex items-center space-x-2">
            <Mail className="text-primary-600" size={24} />
            <span>Admin Notifications</span>
          </h3>
          <p className="text-gray-600 mb-4">
            Choose which appointment events you want to receive email notifications for.
          </p>

          <div className="space-y-4">
            <div className="flex items-center space-x-2">
              <input
                type="checkbox"
                id="adminNotificationsEnabled"
                checked={settings.admin_notifications_enabled}
                onChange={(e) => handleChange('admin_notifications_enabled', e.target.checked)}
                className="w-4 h-4 text-primary-600 rounded"
              />
              <label htmlFor="adminNotificationsEnabled" className="text-sm font-medium text-gray-700">
                Enable admin email notifications
              </label>
            </div>

            {settings.admin_notifications_enabled && (
              <div className="space-y-4">
                {/* Granular event toggles */}
                <div className="bg-gray-50 rounded-lg p-4 space-y-3">
                  <p className="text-sm font-medium text-gray-700 mb-2">Notify me when:</p>
                  {[
                    { key: 'admin_notification_new_booking', label: 'A new appointment is booked (pending approval)' },
                    { key: 'admin_notification_approval', label: 'An appointment is approved / confirmed' },
                    { key: 'admin_notification_cancellation', label: 'An appointment is cancelled' },
                    { key: 'admin_notification_status_change', label: 'An appointment status changes (completed, no-show, etc.)' },
                    { key: 'admin_notification_financial', label: 'A discount or refund is applied' },
                  ].map(({ key, label }) => (
                    <div key={key} className="flex items-center space-x-2">
                      <input
                        type="checkbox"
                        id={key}
                        checked={settings[key] !== false}
                        onChange={(e) => handleChange(key, e.target.checked)}
                        className="w-4 h-4 text-primary-600 rounded"
                      />
                      <label htmlFor={key} className="text-sm text-gray-600">{label}</label>
                    </div>
                  ))}
                </div>

                <div>
                  <label className="label">Admin Notification Email Addresses</label>
                  <p className="text-xs text-gray-500 mb-3">
                    Add one or more email addresses to receive the selected notifications above.
                  </p>
                  <div className="bg-yellow-50 border border-yellow-300 rounded p-2 mb-3">
                    <p className="text-xs text-yellow-800">
                      💡 <strong>Important:</strong> After adding or editing emails, scroll down and click "Save Communication Settings" to activate notifications.
                    </p>
                  </div>

                  {/* Email List */}
                  {settings.admin_notification_email.length > 0 && (
                    <div className="space-y-2 mb-4">
                      {settings.admin_notification_email.map((email, index) => (
                        <div key={index} className="flex items-center space-x-2 p-3 bg-gray-50 rounded-lg">
                          {editingEmailIndex === index ? (
                            <>
                              <input
                                type="email"
                                value={editingEmailValue}
                                onChange={(e) => setEditingEmailValue(e.target.value)}
                                className="input flex-1"
                                placeholder="admin@example.com"
                              />
                              <button
                                onClick={saveEditEmail}
                                className="btn bg-green-100 text-green-700 hover:bg-green-200 p-2"
                                title="Save"
                              >
                                <Check size={16} />
                              </button>
                              <button
                                onClick={cancelEditEmail}
                                className="btn bg-gray-100 text-gray-700 hover:bg-gray-200 p-2"
                                title="Cancel"
                              >
                                <X size={16} />
                              </button>
                            </>
                          ) : (
                            <>
                              <Mail size={16} className="text-gray-500" />
                              <span className="flex-1 text-sm font-medium text-gray-700">{email}</span>
                              <button
                                onClick={() => startEditEmail(index)}
                                className="btn bg-blue-100 text-blue-700 hover:bg-blue-200 p-2"
                                title="Edit"
                              >
                                <Edit2 size={16} />
                              </button>
                              <button
                                onClick={() => deleteEmail(index)}
                                className="btn bg-red-100 text-red-700 hover:bg-red-200 p-2"
                                title="Delete"
                              >
                                <Trash2 size={16} />
                              </button>
                            </>
                          )}
                        </div>
                      ))}
                    </div>
                  )}

                  {/* Add New Email */}
                  <div className="flex items-center space-x-2">
                    <input
                      type="email"
                      value={newEmail}
                      onChange={(e) => setNewEmail(e.target.value)}
                      onKeyPress={(e) => e.key === 'Enter' && addEmail()}
                      placeholder="Add new email address"
                      className="input flex-1"
                    />
                    <button
                      onClick={addEmail}
                      className="btn btn-primary flex items-center space-x-2"
                    >
                      <Plus size={16} />
                      <span>Add</span>
                    </button>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Info Box */}
        <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
          <h4 className="font-semibold text-yellow-900 mb-2">Quick Tips:</h4>
          <ul className="text-sm text-yellow-800 space-y-1">
            <li>• <strong>Gmail / Yahoo:</strong> You must use an App Password — regular passwords will be rejected</li>
            <li>• <strong>Save first, test second:</strong> Always save your settings before sending a test email</li>
            <li>• <strong>SendGrid:</strong> Create an API key at <a href="https://sendgrid.com" target="_blank" rel="noopener noreferrer" className="underline">sendgrid.com</a></li>
            <li>• <strong>Security:</strong> All credentials are stored securely on the server</li>
          </ul>
        </div>

        {/* Save Button */}
        <div className="flex justify-end pt-4">
          <button
            onClick={handleSave}
            disabled={saving}
            className="btn btn-primary flex items-center space-x-2"
          >
            <Save size={18} />
            <span>{saving ? 'Saving...' : 'Save Communication Settings'}</span>
          </button>
        </div>
      </div>
    </div>
  );
};

export default CommunicationSettingsEditor;
